/**
 * Bonndle Authentication API
 * Handles all authentication operations with Boldscale backend
 */

import {
  request,
  safeRequest,
  publicRequest,
  getAuthToken,
  setAuthToken,
  getStoredUser,
  storeUserData,
  clearAuth,
  isAuthenticated,
  getApiUrl,
  STORAGE_KEYS,
} from './bonndleApi';

const API_URL = getApiUrl();

/**
 * Authentication API module
 */
export const bonndleAuthApi = {
  /**
   * Login with email/phone and PIN
   * Boldscale: POST /login
   */
  async login({ email, phone, pin }) {
    const payload = { pin };

    // Add either email or phone (not both)
    if (email) {
      payload.email = email;
    } else if (phone) {
      payload.phone = phone;
    }

    console.log('Login payload:', JSON.stringify(payload));

    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('Login error response:', error);
      throw new Error(error.message || error.detail || 'Login failed');
    }

    const data = await response.json();

    // DEBUG: Log response for troubleshooting
    console.log('[DEBUG] Login response:', {
      hasToken: !!data.token,
      hasAccessToken: !!data.access_token,
      hasUser: !!data.user,
      hasUserUuid: !!data.user_uuid,
      hasFirstName: !!data.first_name,
      keys: Object.keys(data),
    });

    // Store auth data - API returns 'token' not 'access_token'
    const token = data.token || data.access_token;
    if (token) {
      setAuthToken(token);
    }

    // Store user data - API may return user nested or at top level
    if (data.user) {
      storeUserData(data.user);
    } else if (data.user_uuid || data.id || data.uuid || data.first_name) {
      // User data is at top level of response
      storeUserData(data);
    }

    // Return user data in normalized format for immediate use
    const user = data.user || (data.user_uuid || data.first_name ? data : null);
    return { ...data, user };
  },

  /**
   * Register new user
   * Boldscale: POST /register
   * @param {object} userData - User registration data
   * @param {string} userData.phone - Phone number with country code (e.g., +2348012345678)
   * @param {string} [userData.email] - Email address
   * @param {string} userData.firstName - First name
   * @param {string} userData.lastName - Last name
   * @param {string} [userData.dateOfBirth] - Date of birth (YYYY-MM-DD)
   * @param {string} [userData.gender] - Gender
   * @param {string} userData.country - Country name (e.g., "Nigeria")
   * @param {string} userData.boldTag - Bonndle tag (username)
   * @param {string} userData.pin - 4-digit PIN
   * @param {string} [userData.referralCode] - Referral code
   * @param {string} [userData.phoneVerificationToken] - Firebase ID token for phone verification
   * @param {string} [userData.otpId] - OTP session ID (Firebase verificationId or Termii pinId)
   * @param {string} [userData.otp] - OTP code entered by user
   * @param {string} [userData.guestId] - Guest ID if converting from guest
   */
  async register(userData) {
    // Parse phone number to extract code and digits
    let phoneNumberCode = '';
    let otherPhoneNumberDigits = '';
    if (userData.phone) {
      // Handle formats like +2348012345678 or 2348012345678
      const phone = userData.phone.startsWith('+') ? userData.phone : `+${userData.phone}`;
      // Extract country code (assume first 4 chars for +234, etc.)
      if (phone.startsWith('+234')) {
        phoneNumberCode = '+234';
        otherPhoneNumberDigits = phone.slice(4);
      } else if (phone.startsWith('+1')) {
        phoneNumberCode = '+1';
        otherPhoneNumberDigits = phone.slice(2);
      } else {
        // Default: try to extract based on common patterns
        const match = phone.match(/^(\+\d{1,4})(\d+)$/);
        if (match) {
          phoneNumberCode = match[1];
          otherPhoneNumberDigits = match[2];
        }
      }
    }

    // Generate installation ID if not already stored
    let installationId = localStorage.getItem('installation_id');
    if (!installationId) {
      installationId = crypto.randomUUID();
      localStorage.setItem('installation_id', installationId);
    }

    const payload = {
      phone: userData.phone || undefined,
      email: userData.email || undefined,
      firstName: userData.firstName,
      lastName: userData.lastName,
      dateOfBirth: userData.dateOfBirth || '2000-01-01', // Default date of birth if not provided
      gender: userData.gender || undefined,
      country: userData.country || 'Nigeria',
      phoneNumberCode: phoneNumberCode || undefined,
      otherPhoneNumberDigits: otherPhoneNumberDigits || undefined,
      boldTag: userData.boldTag,
      pin: userData.pin,
      deviceToken: localStorage.getItem('fcm_token') || '',
      imei: '',
      installationId: installationId,
      buildId: import.meta.env.VITE_APP_VERSION || '1.0.0',
      emailVerified: true,
      userType: userData.userType || 'individual',
      referredBy: userData.referralCode || undefined,
      // Phone verification fields (Firebase/Termii)
      phoneVerificationToken: userData.phoneVerificationToken || undefined,
      otpId: userData.otpId || undefined,
      otp: userData.otp || undefined,
      // Guest conversion
      guestId: userData.guestId || undefined,
    };

    // Remove undefined values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    console.log('[DEBUG] Register payload:', {
      ...payload,
      pin: '****',
    });

    const data = await publicRequest('/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    // DEBUG: Log response for troubleshooting
    console.log('[DEBUG] Register response:', {
      hasToken: !!data.token,
      hasAccessToken: !!data.access_token,
      hasUser: !!data.user,
      hasUserUuid: !!data.user_uuid,
      keys: Object.keys(data),
    });

    // Store auth data after registration - API may return 'token' or 'access_token'
    const token = data.token || data.access_token;
    if (token) {
      setAuthToken(token);
    }

    // Store user data - API may return user nested or at top level
    if (data.user) {
      storeUserData(data.user);
    } else if (data.user_uuid || data.id || data.uuid) {
      // User data is at top level of response
      storeUserData(data);
    }

    // Also return user data in normalized format for immediate use
    const user = data.user || (data.user_uuid ? data : null);
    return { ...data, user };
  },

  /**
   * Store BoldTag during registration
   * Boldscale: POST /register/store-bold-tag
   */
  async storeBoldTag(boldTag) {
    return request('/register/store-bold-tag', {
      method: 'POST',
      body: JSON.stringify({ boldTag }),
    });
  },

  /**
   * Verify BoldTag availability
   * Boldscale: POST /register/check-bold-tag
   */
  async checkBoldTag(boldTag) {
    return publicRequest('/register/check-bold-tag', {
      method: 'POST',
      body: JSON.stringify({ boldTag }),
    });
  },

  /**
   * Verify PIN (for authenticated users)
   * Boldscale: POST /verify-pin
   */
  async verifyPin(pin) {
    return request('/verify-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  /**
   * Login with email and PIN verification
   * For email login flow: check email exists, then verify PIN
   * Boldscale: POST /verify-pin with email
   */
  async loginWithEmailPin({ email, pin }) {
    console.log('Login with email PIN:', { email, pin: '****' });

    const response = await fetch(`${API_URL}/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, pin }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('Email login error response:', error);
      throw new Error(error.message || error.detail || 'Login failed');
    }

    const data = await response.json();

    // Store auth data
    const token = data.token || data.access_token;
    if (token) {
      setAuthToken(token);
    }

    if (data.user) {
      storeUserData(data.user);
    }

    return data;
  },

  /**
   * Create PIN (for new users after registration)
   * Boldscale: POST /create-pin
   */
  async createPin(pin) {
    return request('/create-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  /**
   * Reset PIN (for authenticated users)
   * Boldscale: POST /reset-pin
   */
  async resetPin({ currentPin, newPin }) {
    return request('/reset-pin', {
      method: 'POST',
      body: JSON.stringify({
        current_pin: currentPin,
        new_pin: newPin,
      }),
    });
  },

  /**
   * Reset PIN for social login users
   * Boldscale: POST /social-reset-pin
   */
  async socialResetPin({ otp, newPin }) {
    return request('/social-reset-pin', {
      method: 'POST',
      body: JSON.stringify({
        otp,
        new_pin: newPin,
      }),
    });
  },

  /**
   * Send OTP for verification or reset
   * Boldscale: POST /send-otp
   */
  async sendOtp({ email, phone }) {
    const payload = {};
    if (email) payload.email = email;
    if (phone) payload.phone = phone;

    return publicRequest('/send-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Send OTP for social sign-up PIN creation
   * Boldscale: POST /send-otp (authenticated)
   * Called after social registration to send OTP for PIN setup
   */
  async sendSocialOtp() {
    return request('/send-otp', {
      method: 'POST',
    });
  },

  /**
   * Create PIN for social users after OTP verification
   * Boldscale: POST /create-pin
   * @param {string} pin - The PIN to create
   * @param {string} otp - The OTP code for verification
   */
  async createSocialPin(pin, otp) {
    return request('/create-pin', {
      method: 'POST',
      body: JSON.stringify({ pin, otp }),
    });
  },

  /**
   * Check if phone number exists
   * Boldscale: POST /check-phone
   */
  async checkPhone(phone) {
    return publicRequest('/check-phone', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  },

  /**
   * Check if email exists
   * Boldscale: POST /check-email
   */
  async checkEmail(email) {
    return publicRequest('/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Check referral code validity
   * Boldscale: POST /check-referral-code
   */
  async checkReferralCode(code) {
    return publicRequest('/check-referral-code', {
      method: 'POST',
      body: JSON.stringify({ referral_code: code }),
    });
  },

  /**
   * Google Sign-In
   * Boldscale: POST /register/google
   * @param {string} idToken - Google ID token JWT
   * @param {object} options - Additional options (country, deviceToken, etc.)
   */
  async googleSignIn(idToken, options = {}) {
    const payload = {
      idToken,
      deviceToken: options.deviceToken || localStorage.getItem('fcm_token') || '',
      imei: options.imei || '',
      installation_id: options.installation_id || localStorage.getItem('installation_id') || crypto.randomUUID(),
      build_id: options.build_id || import.meta.env.VITE_APP_VERSION || '1.0.0',
      country: options.country || '',
    };

    // Store installation_id for future use
    if (!localStorage.getItem('installation_id')) {
      localStorage.setItem('installation_id', payload.installation_id);
    }

    // DEBUG: Log token for troubleshooting
    console.log('[DEBUG] Google Sign-In payload:', {
      idToken: idToken ? `${idToken.substring(0, 50)}...` : 'MISSING',
      tokenLength: idToken?.length,
      country: payload.country,
    });

    const response = await fetch(`${API_URL}/register/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.detail || 'Google sign-in failed');
    }

    const data = await response.json();

    // DEBUG: Log response for troubleshooting
    console.log('[DEBUG] Google Sign-In response:', {
      hasToken: !!data.token,
      hasAccessToken: !!data.access_token,
      hasUser: !!data.user,
      keys: Object.keys(data),
    });

    // Store auth data - API may return 'token' or 'access_token'
    const token = data.token || data.access_token;
    if (token) {
      setAuthToken(token);
    }
    if (data.user) {
      storeUserData(data.user);
    }

    return data;
  },

  /**
   * Apple Sign-In
   * Boldscale: POST /register/apple
   * @param {string} identityToken - Apple identity token (JWT)
   * @param {object} options - Additional options
   * @param {object} options.user - User info from Apple (only on first sign-in)
   * @param {string} options.country - Country code
   */
  async appleSignIn(identityToken, options = {}) {
    // Generate installation ID if not already stored
    let installationId = localStorage.getItem('installation_id');
    if (!installationId) {
      installationId = crypto.randomUUID();
      localStorage.setItem('installation_id', installationId);
    }

    const payload = {
      identity_token: identityToken,
      deviceToken: localStorage.getItem('fcm_token') || '',
      imei: '',
      installation_id: installationId,
      build_id: import.meta.env.VITE_APP_VERSION || '1.0.0',
      country: options.country || '',
    };

    // Apple provides user info only on the first sign-in
    // Pass it to the backend for profile creation
    if (options.user) {
      payload.first_name = options.user.name?.firstName || '';
      payload.last_name = options.user.name?.lastName || '';
      payload.email = options.user.email || '';
    }

    // DEBUG: Log payload for troubleshooting
    console.log('[DEBUG] Apple Sign-In payload:', {
      identity_token: identityToken ? `${identityToken.substring(0, 50)}...` : 'MISSING',
      tokenLength: identityToken?.length,
      country: payload.country,
      hasUserInfo: !!options.user,
    });

    const response = await fetch(`${API_URL}/register/apple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.detail || 'Apple sign-in failed');
    }

    const data = await response.json();

    // DEBUG: Log response for troubleshooting
    console.log('[DEBUG] Apple Sign-In response:', {
      hasToken: !!data.token,
      hasAccessToken: !!data.access_token,
      hasUser: !!data.user,
      keys: Object.keys(data),
    });

    // Store auth data - API may return 'token' or 'access_token'
    const token = data.token || data.access_token;
    if (token) {
      setAuthToken(token);
    }
    if (data.user) {
      storeUserData(data.user);
    }

    return data;
  },

  /**
   * Guest registration
   * Boldscale: POST /register/guest
   */
  async registerGuest(guestData = {}) {
    return publicRequest('/register/guest', {
      method: 'POST',
      body: JSON.stringify(guestData),
    });
  },

  /**
   * Add guest
   * Boldscale: POST /guest
   */
  async addGuest(guestData = {}) {
    return publicRequest('/guest', {
      method: 'POST',
      body: JSON.stringify(guestData),
    });
  },

  /**
   * Refresh access token
   * Boldscale: POST /refresh-token
   */
  async refreshToken() {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No token to refresh');
    }

    const response = await fetch(`${API_URL}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    if (data.access_token) {
      setAuthToken(data.access_token);
    }

    return data;
  },

  /**
   * Logout - Clear local auth state
   * Note: Boldscale doesn't have a server-side logout endpoint
   */
  logout() {
    clearAuth();
    return Promise.resolve({ success: true });
  },

  /**
   * Get current user from storage
   */
  getCurrentUser() {
    return getStoredUser();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return isAuthenticated();
  },

  /**
   * Get user profile from server
   * Boldscale: GET /users/{userId}
   * Uses safeRequest to avoid logout on failure
   */
  async getProfile() {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    console.log('[getProfile] User ID from storage:', userId);
    if (!userId) {
      throw new Error('No user ID found');
    }

    const user = await safeRequest(`/users/${userId}`);
    console.log('[getProfile] Fetched user profile:', user?.first_name, user?.last_name);
    storeUserData(user);
    return user;
  },

  /**
   * Update user profile
   * Boldscale: PUT /users/{userId}
   */
  async updateProfile(updates) {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    console.log('[updateProfile] User ID:', userId);
    console.log('[updateProfile] Updates payload:', updates);

    if (!userId) {
      throw new Error('No user ID found');
    }

    console.log('[updateProfile] Making PUT request to /users/' + userId);
    const user = await request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    console.log('[updateProfile] Response:', user);
    storeUserData(user);
    return user;
  },

  /**
   * Get points earned today
   * Boldscale: GET /transactions/points-reward-earned-today
   * Uses safeRequest to avoid logout on failure
   */
  async getPointsEarnedToday() {
    return safeRequest('/transactions/points-reward-earned-today');
  },

  /**
   * Get recent transactions
   * Boldscale: GET /transactions/recent-transactions
   * Uses safeRequest to avoid logout on failure
   */
  async getRecentTransactions() {
    return safeRequest('/transactions/recent-transactions');
  },

  /**
   * Get all transactions with pagination
   * Boldscale: GET /transactions
   * Uses safeRequest to avoid logout on failure
   */
  async getTransactions(page = 1, limit = 20) {
    return safeRequest(`/transactions?page=${page}&limit=${limit}`);
  },

  /**
   * Get reward/transaction history
   * Fetches both transactions and points data
   */
  async getRewardHistory(page = 1, limit = 20) {
    const [transactions, pointsToday] = await Promise.all([
      this.getTransactions(page, limit),
      this.getPointsEarnedToday().catch(() => ({ totalPoints: 0 })),
    ]);

    return {
      // Transactions come as array directly, not nested in data
      transactions: Array.isArray(transactions) ? transactions : (transactions.data || []),
      // API returns 'totalPoints' not 'points'
      pointsEarnedToday: pointsToday.totalPoints || pointsToday.points || 0,
      pagination: {
        page,
        limit,
        total: Array.isArray(transactions) ? transactions.length : (transactions.total || 0),
      },
    };
  },

  /**
   * Send Bold Credits to another user
   * Boldscale: POST /bold-credits/send
   */
  async sendBoldCredits({ recipientId, amount, note }) {
    return request('/bold-credits/send', {
      method: 'POST',
      body: JSON.stringify({
        recipient_id: recipientId,
        amount,
        note,
      }),
    });
  },

  /**
   * Redeem Bold Credits
   * Boldscale: POST /bold-credits/redeem
   */
  async redeemBoldCredits({ amount, redemptionType }) {
    return request('/bold-credits/redeem', {
      method: 'POST',
      body: JSON.stringify({
        amount,
        redemption_type: redemptionType,
      }),
    });
  },

  /**
   * Get user's saved cards
   * Boldscale: GET /cards
   */
  async getCards() {
    return safeRequest('/cards');
  },

  /**
   * Register device for push notifications
   * Boldscale: POST /users/:uuid/devices
   * Call after login and whenever FCM token is refreshed
   * @param {string} token - FCM token from Firebase SDK
   * @param {string} [platform] - Platform type (web, ios, android, mobile). Defaults to 'web'
   */
  async registerDevice(token, platform = 'web') {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!userId) {
      throw new Error('No user ID found. User must be logged in to register device.');
    }

    return request(`/users/${userId}/devices`, {
      method: 'POST',
      body: JSON.stringify({
        token,
        platform,
      }),
    });
  },

  /**
   * Verify BVN for identity verification
   * Boldscale: PUT /users/:uuid/verify-bvn
   * @param {string} bvn - The BVN number to verify
   */
  async verifyBVN(bvn) {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!userId) {
      throw new Error('No user ID found. User must be logged in to verify BVN.');
    }

    return request(`/users/${userId}/verify-bvn`, {
      method: 'PUT',
      body: JSON.stringify({ bvn }),
    });
  },

  /**
   * Upload verification document (for non-BVN/NIN verification)
   * Boldscale: PUT /users/:uuid/update-verification-document
   * @param {File} file - The document file to upload
   */
  async uploadVerificationDocument(file) {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!userId) {
      throw new Error('No user ID found. User must be logged in to upload document.');
    }

    const formData = new FormData();
    formData.append('document', file);

    const token = getAuthToken();
    const API_URL = getApiUrl();

    const response = await fetch(`${API_URL}/users/${userId}/update-verification-document`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.detail || 'Document upload failed');
    }

    return response.json();
  },
};

export default bonndleAuthApi;
